import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import { configuracaoDoProduto } from '../utils/estoqueMinimo';

const TITLE_ID = 'estoque-minimo-title';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const formatarSaldo = (valor) => Number(valor || 0).toLocaleString('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const VALOR_INVALIDO = Symbol('valor inválido');

/**
 * Campo vazio e campo com `0` são coisas diferentes e precisam continuar diferentes até o backend:
 * vazio herda, zero silencia. Por isso o estado guarda string, e só na hora de enviar vira
 * `null` ou número.
 *
 * Entrada inválida tem um terceiro retorno de propósito: `null` apaga a configuração, então um
 * "-5" digitado por engano (o campo number não bloqueia o sinal) apagaria em silêncio justamente
 * o mínimo que a pessoa foi ajustar.
 */
const paraEnvio = (texto) => {
  const limpo = String(texto ?? '').trim().replace(',', '.');

  if (limpo === '') {
    return null;
  }

  const numero = Number(limpo);
  return Number.isFinite(numero) && numero >= 0 ? numero : VALOR_INVALIDO;
};

const paraCampo = (valor) => (valor === null || valor === undefined ? '' : String(valor));

const EstoqueMinimoModal = ({
  produto,
  almoxarifados = [],
  minimos = [],
  onSalvar,
  onClose,
}) => {
  const [padrao, setPadrao] = useState('');
  const [excecoes, setExcecoes] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);

  const idProduto = produto?.id_produto ?? null;

  const configuracao = useMemo(
    () => configuracaoDoProduto(minimos, idProduto),
    [minimos, idProduto],
  );

  useEffect(() => {
    if (idProduto === null) {
      return;
    }

    setPadrao(paraCampo(configuracao.padrao));
    setExcecoes(Object.fromEntries(
      Object.entries(configuracao.excecoes).map(([id, valor]) => [id, paraCampo(valor)]),
    ));
    setErro('');
  }, [configuracao, idProduto]);

  // Trava a rolagem de fundo, devolve o foco ao fechar e mantém Tab dentro do modal.
  useEffect(() => {
    if (idProduto === null || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';

    window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) {
        return;
      }

      const focusables = panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (!first || !last) {
        event.preventDefault();
        return;
      }

      if (!panelRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [idProduto, onClose]);

  const salvar = useCallback(async () => {
    setSalvando(true);
    setErro('');

    try {
      const padraoConvertido = paraEnvio(padrao);
      const excecoesConvertidas = almoxarifados
        .filter((almox) => almox.id_almox !== null && almox.id_almox !== undefined)
        .map((almox) => ({
          id_almox: almox.id_almox,
          minimo: paraEnvio(excecoes[almox.id_almox]),
          almox_descricao: almox.descricao,
        }));

      if (padraoConvertido === VALOR_INVALIDO
        || excecoesConvertidas.some((excecao) => excecao.minimo === VALOR_INVALIDO)) {
        setErro('Informe apenas números maiores ou iguais a zero.');

        return;
      }

      await onSalvar({
        padrao: padraoConvertido,
        produto_descricao: produto?.Produto || `Produto ${idProduto}`,
        excecoes: excecoesConvertidas,
      });

      onClose?.();
    } catch (error) {
      setErro(error?.response?.data?.message || error?.message || 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }, [almoxarifados, excecoes, idProduto, onClose, onSalvar, padrao, produto]);

  if (idProduto === null) {
    return null;
  }

  // Valor inválido conta como ausência aqui: só alimenta o "herda X" e o placeholder.
  const padraoDigitado = paraEnvio(padrao);
  const padraoNumerico = padraoDigitado === VALOR_INVALIDO ? null : padraoDigitado;

  return (
    <>
      <div className="fixed inset-0 z-[119] bg-slate-950/78" onClick={onClose} aria-hidden="true" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        data-testid="estoque-minimo-modal"
        className="fixed inset-x-4 top-[calc(var(--header-shell-height)+12px)] bottom-4 z-[120] flex flex-col overflow-hidden rounded-[28px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] p-4 shadow-[0_28px_80px_rgba(2,6,23,0.45)] sm:inset-x-8 sm:bottom-6 sm:p-5 lg:inset-x-1/4"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 id={TITLE_ID} className="text-lg font-semibold text-[color:var(--text-primary)]">
              Estoque mínimo
            </h3>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              {`#${idProduto} · ${produto?.Produto || 'Produto'}`}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Fechar estoque mínimo"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {erro ? (
          <div className="mb-3 flex items-start gap-2 rounded-[12px] border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs leading-5 text-[color:var(--text-muted)]">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <span>{erro}</span>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="rounded-[16px] border border-[color:var(--border)] bg-[color:var(--surface)] p-3">
            <label
              htmlFor="estoque-minimo-padrao"
              className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--text-muted)]"
            >
              Mínimo padrão do produto
            </label>
            <input
              id="estoque-minimo-padrao"
              type="number"
              min="0"
              step="any"
              value={padrao}
              onChange={(event) => setPadrao(event.target.value)}
              placeholder="Sem mínimo"
              className="mt-2 w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2 text-sm text-[color:var(--text-primary)] outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/20"
            />
            <p className="mt-2 text-[11px] leading-4 text-[color:var(--text-muted)]">
              Vale para todos os almoxarifados que não tiverem exceção. Deixe vazio para o produto
              não ter mínimo em lugar nenhum.
            </p>
          </div>

          <div className="rounded-[16px] border border-[color:var(--border)] bg-[color:var(--surface)] p-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--text-muted)]">
              Exceções por almoxarifado
            </p>
            <p className="mt-1 text-[11px] leading-4 text-[color:var(--text-muted)]">
              Vazio herda o padrão. <strong>Zero</strong> significa que ali não se cobra mínimo —
              use para item que a cidade não trabalha, em vez de conviver com alerta permanente.
            </p>

            <ul className="mt-3 space-y-2">
              {almoxarifados.map((almox) => {
                const valor = excecoes[almox.id_almox] ?? '';
                const herdado = valor.trim() === '' && padraoNumerico !== null;

                return (
                  <li key={almox.id_almox} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-[color:var(--text-primary)]">{almox.descricao}</p>
                      <p className="text-[11px] text-[color:var(--text-muted)]">
                        {`Saldo atual: ${formatarSaldo(almox.saldo)}`}
                        {herdado ? ` · herda ${padraoNumerico.toLocaleString('pt-BR')}` : ''}
                      </p>
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      aria-label={`Mínimo em ${almox.descricao}`}
                      value={valor}
                      onChange={(event) => setExcecoes((atual) => ({
                        ...atual,
                        [almox.id_almox]: event.target.value,
                      }))}
                      placeholder={padraoNumerico !== null ? String(padraoNumerico) : 'Sem mínimo'}
                      className="w-28 shrink-0 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2 text-right text-sm text-[color:var(--text-primary)] outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/20"
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-[color:var(--header-control-border-subtle)] px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={salvar}
            disabled={salvando}
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--accent)] px-4 py-2 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
          >
            {salvando ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
            Salvar
          </button>
        </div>
      </div>
    </>
  );
};

export default EstoqueMinimoModal;
